import { getSupportedDevicesSync } from "@eweadn-qiankun/shared-utils";

/** Alpha7 接收器灯：固件/UI 不提供单色常亮、单色呼吸与调色盘 */
export function deviceHidesReceiverMonoAndPalette(
  deviceId: string | undefined
): boolean {
  if (!deviceId) return false;
  const id = deviceId.toLowerCase();
  return id === "alpha7" || id === "alpha7-2.4g";
}

/** 当前 deviceId 是否存在支持接收器灯的型号（含 2.4G 变体） */
export function deviceSupportsDongleLight(
  deviceId: string | undefined
): boolean {
  if (!deviceId) return true;

  const rows = (
    getSupportedDevicesSync().mouse as Array<{
      deviceId?: string;
      noDongleLight?: boolean;
    }>
  ).filter((d) => d.deviceId === deviceId);

  if (!rows.length) return true;
  return rows.some((d) => d.noDongleLight !== true);
}

/**
 * 各鼠标方案接收器灯光 UI / 回退默认：
 * mode 1 = 彩色流动；亮度/速度 100%；红
 * （恢复默认、进页占位、拉灯失败回退共用）
 */
export const DEFAULT_DONGLE_RGB_BAR = {
  mode: 1,
  brightness: 100,
  speed: 100,
  color: "#FF0000",
};

export interface LightModeDisables {
  color: boolean;
  brightness: boolean;
  speed: boolean;
}

/** 接收器灯效模式对应的禁用项 */
export function lightModeToDisable(
  mode: number,
  scheme: "jiangmeng" | "zhanli" = "jiangmeng"
): LightModeDisables {
  const disables: LightModeDisables = {
    color: false,
    brightness: false,
    speed: false,
  };

  if (scheme === "zhanli") {
    // 展立：2=单色常亮 3=单色呼吸
    switch (mode) {
      case 0:
        disables.color = true;
        disables.brightness = true;
        disables.speed = true;
        break;
      case 1:
      case 4:
      case 5:
        disables.color = true;
        break;
      case 6:
        disables.color = true;
        disables.speed = true;
        break;
      case 2:
        disables.speed = true;
        break;
      default:
        break;
    }
    return disables;
  }

  // 匠盟：2=单色呼吸 3=单色常亮
  switch (mode) {
    case 0:
      disables.color = true;
      disables.brightness = true;
      disables.speed = true;
      break;
    case 1:
    case 4:
    case 5:
      disables.color = true;
      break;
    case 6:
      disables.color = true;
      disables.speed = true;
      break;
    case 3:
      disables.speed = true;
      break;
    default:
      break;
  }

  return disables;
}

export function normalizeHexColor(raw: unknown): string {
  const text = String(raw || "#ff0000").trim();
  if (!text) return "#ff0000";
  if (text.startsWith("#")) return text;
  if (text.startsWith("rgb")) {
    const match = text.match(/\d+/g);
    if (match && match.length >= 3) {
      const [r, g, b] = match.map((n) =>
        Math.max(0, Math.min(255, Number(n) || 0))
      );
      const toHex = (n: number) => n.toString(16).padStart(2, "0");
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    }
  }
  return `#${text.replace("#", "")}`;
}

export function clampPercent(value: unknown, fallback = 100): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(100, Math.max(10, Math.round(n / 10) * 10));
}

/** 展立接收器灯（0x0A/0x1B）：协议亮度/速度 0~100，UI 与下发同值 */
export function clampZhanliDonglePercent(
  value: unknown,
  fallback = 100
): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(100, Math.max(0, Math.trunc(n)));
}

/** 匠盟：设备档 0–9 → UI 10%–100% */
export function jiangmengLevelToUiPercent(level: number): number {
  return clampPercent((Number(level) + 1) * 10);
}

/** 匠盟：UI 10%–100% → 设备档 0–9 */
export function uiPercentToJiangmengLevel(percent: number): number {
  return clampPercent(percent) / 10 - 1;
}
